import axios from "axios";
import { ImagePickerAsset } from "expo-image-picker";
import { useCallback, useState } from "react";
import { Platform } from "react-native";

import { FileExtension, StorageFolder } from "@/types/utils";
import { usePresignedUrl } from "./urls";

interface FileUploadOptions {
  file: ImagePickerAsset;
  folder: StorageFolder;
  onProgress?: (progress: number) => void;
  /** The study or adhoc question the file belongs to, for the folders that
      live under one. The question type for `adhoc`. */
  resourceId?: string;
}

const useFileUpload = () => {
  const [loading, setLoading] = useState(false);

  const { getPresignedUrl } = usePresignedUrl();

  const uploadFile = useCallback(
    async (options: FileUploadOptions): Promise<string> => {
      const { file, folder, onProgress, resourceId } = options;

      const mimeType = file.mimeType || "image/jpeg";
      // Handle video MIME types (video/mp4 -> mp4, video/quicktime -> mov)
      let fileExtension: FileExtension;
      const mimeSubtype = mimeType.split("/")[1]?.toLowerCase();
      if (mimeSubtype === "quicktime") {
        fileExtension = "mov";
      } else {
        fileExtension = (mimeSubtype as FileExtension) ?? "jpeg";
      }

      const isVideo = mimeType.startsWith("video/");
      const defaultFileName = isVideo
        ? `video.${fileExtension}`
        : `image.${fileExtension}`;
      const fileName = file.fileName || defaultFileName;

      setLoading(true);
      try {
        const { url_data, file_path } = await getPresignedUrl({
          file_extension: fileExtension,
          folder,
          resource_id: resourceId,
        });

        // Create FormData for S3 upload
        const formData = new FormData();
        // Add all the fields from the presigned URL
        for (const key in url_data.fields) {
          formData.append(key, url_data.fields[key]);
        }
        // Add the file last (S3 requirement)
        if (
          Platform.OS === "web" &&
          (file.uri.startsWith("data:") ||
            file.uri.startsWith("blob:") ||
            file.uri.startsWith("http"))
        ) {
          // Web: Convert data URL, blob URL, or HTTP URL to Blob
          const response = await fetch(file.uri);
          const blob = await response.blob();
          formData.append("file", blob, fileName);
        } else {
          // Mobile: Use the standard format
          formData.append("file", {
            uri: file.uri,
            name: fileName,
            type: mimeType,
          } as never);
        }

        // Upload to S3 with progress tracking
        await axios.post(url_data.url, formData, {
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const completed =
                Math.round((progressEvent.loaded * 100) / progressEvent.total) /
                100;
              if (onProgress) {
                onProgress(completed);
              }
            }
          },
        });
        return file_path;
      } catch (e) {
        if (e instanceof Error) {
          throw Error(e.message);
        } else {
          throw Error(String(e));
        }
      } finally {
        setLoading(false);
      }
    },
    [getPresignedUrl]
  );

  return {
    loading,
    uploadFile,
  };
};

export { useFileUpload };
